<template>
  <Modal
    :visible="visible"
    :overlay="false"
    :class="`${className} !tw-max-w-[720px] tw-top-[54%]`"
  >
    <ModalHeader :title="title" @close="$emit('close')" />
    <div
      class="cropper-container tw-justify-around tw-flex tw-flex-col tw-p-[40px] tw-gap-[40px]"
    >
      <div class="tw-flex tw-flex-col tw-gap-[20px] cropper">
        <div class="main tw-relative">
          <Cropper
            ref="cropper"
            :class="`cropper ${
              isMacOS
                ? 'tw-cursor-grab active:tw-cursor-grabbing'
                : 'tw-cursor-move'
            }`"
            background-class="cropper__background"
            foreground-class="cropper__foreground"
            :stencil-component="
              useRectangleStencil ? RectangleStencil : CircleStencil
            "
            :src="src"
            :stencil-size="{
              width: 600,
              height: useRectangleStencil ? 240 : 600,
            }"
            :stencil-props="{
              handlers: {},
              movable: false,
              resizable: false,
              lines: {
                north: false,
                west: false,
                south: false,
                east: false,
              },
            }"
            :resize-image="{
              adjustStencil: false,
            }"
            image-restriction="none"
          />
          <div v-if="useRectangleStencil" class="overlay-rectangle"></div>
          <div v-else class="overlay-circle"></div>
          <div id="drag-btn" class="tw-absolute tw-top-2 tw-left-2 tw-flex">
            <div
              class="tw-w-[30px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-bg-white tw-rounded-[6px] tw-cursor-pointer"
              @mouseover="dragBtnColor = '#387DFF'"
              @mouseleave="dragBtnColor = '#B7B7B7'"
            >
              <DragIcon :color="dragBtnColor" />
            </div>
          </div>
          <div
            class="tw-absolute tw-top-2 tw-right-2 tw-flex tw-flex-col tw-gap-[4px]"
          >
            <div
              class="tw-w-[30px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-bg-white tw-rounded-[6px] tw-cursor-pointer"
              @click="zoomIn"
              @mouseover="zoomInBtnColor = '#387DFF'"
              @mouseleave="zoomInBtnColor = '#B7B7B7'"
              @mousedown="zoomInBtnColor = '#211F58'"
              @mouseup="zoomInBtnColor = '#387DFF'"
            >
              <ZoomInIcon :color="zoomInBtnColor" />
            </div>
            <div
              class="tw-w-[30px] tw-h-[30px] tw-flex tw-justify-center tw-items-center tw-bg-white tw-rounded-[6px] tw-cursor-pointer"
              @click="zoomOut"
              @mouseover="zoomOutBtnColor = '#387DFF'"
              @mouseleave="zoomOutBtnColor = '#B7B7B7'"
              @mousedown="zoomOutBtnColor = '#211F58'"
              @mouseup="zoomOutBtnColor = '#387DFF'"
            >
              <ZoomOutIcon :color="zoomOutBtnColor" />
            </div>
          </div>
          <b-popover
            class="custom-cropper-popover"
            target="drag-btn"
            placement="bottom-end"
            trigger="hover"
          >
            <div class="tw-text-[14px] tw-font-[500] tw-text-[#211F58]">
              {{
                getComponentUiText(
                  "app-settings-organisation-profile-avatar-tooltip-header-1",
                )
              }}
            </div>
            <div
              class="tw-text-[13px] tw-font-[400] tw-text-[#231F20] tw-opacity-60"
            >
              {{
                getComponentUiText(
                  "app-settings-organisation-profile-avatar-tooltip-body-1",
                )
              }}
            </div>
          </b-popover>
        </div>
      </div>
      <div
        class="tw-flex tw-flex-col tw-gap-[40px] md:tw-gap-[unset] md:tw-flex-row md:tw-justify-between tw-items-center action_wrapper"
      >
        <span
          class="tw-flex tw-flex-col tw-h-[35px] tw-justify-between requirements"
        >
          <span
            v-if="formats"
            class="tw-text-[#878787] tw-font-Poppins tw-text-[12px] tw-font-normal tw-font-[400] tw-leading-[135%]"
            >{{
              getComponentUiText(
                "app-settings-organisation-profile-avatar-cropper-image-requirements-1",
              )
            }}</span
          >
          <span
            v-if="formats"
            class="tw-text-[#B7B7B7] tw-font-Poppins tw-text-[11px] tw-font-normal tw-font-[400] tw-leading-[135%]"
            >{{ formats }}</span
          >
          <span
            v-if="recommendations"
            class="tw-text-[#B7B7B7] tw-font-Poppins tw-text-[11px] tw-font-normal tw-font-[400] tw-leading-[135%] tw-w-[330px]"
            >{{ recommendations }}</span
          >
        </span>
        <span
          class="tw-flex tw-items-center tw-flex-wrap tw-gap-[14px] actions"
        >
          <Button2
            class="tw-text-center tw-font-Poppins tw-text-[15px] tw-font-[500] tw-leading-[100%] !tw-py-[12px] !tw-px-[18px]"
            :title="getComponentUiText('app-common-action-cancel-button-1')"
            :disabled="isProcessing"
            @click="
              () => {
                $emit('close');
                cropScaleValue = 1;
              }
            "
          />
          <Button1
            class="tw-text-center tw-font-Poppins tw-text-[15px] tw-font-[500] tw-leading-[100%] !tw-py-[12px] !tw-px-[18px]"
            :title="getComponentUiText('app-common-action-upload-button-1')"
            :disabled="isProcessing"
            data-cy="image-cropper-upload-button"
            @click="setCroppedImg"
          />
        </span>
      </div>
    </div>
  </Modal>
  <div v-if="visible" class="overlay"></div>
</template>

<script lang="ts" setup>
import { ref, Ref, watch, ComponentPublicInstance } from "vue";
import Modal from "./modal.vue";
import ModalHeader from "./modalHeader.vue";
import Button2 from "@/components/common/button2.vue";
import Button1 from "@/components/common/button1.vue";
import { Cropper, RectangleStencil, CircleStencil } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
// import "vue-advanced-cropper/dist/theme.compact.css";
import { getComponentUiText } from "@/utils/utilities";
import ZoomInIcon from "@/components/svgs/zoomInIcon.vue";
import ZoomOutIcon from "@/components/svgs/zoomOutIcon.vue";
import DragIcon from "@/components/svgs/dragIcon.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  src: {
    type: String,
    default: null,
  },
  srcType: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: "",
  },
  formats: {
    type: String,
    default: "",
  },
  recommendations: {
    type: String,
    default: "",
  },
  isSmallScreen: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: true,
  },
  useRectangleStencil: {
    type: Boolean,
    default: false,
  },
  className: {
    type: String,
    required: false,
    default: "",
  },
});

const cropper: ComponentPublicInstance<typeof Cropper> | Ref = ref();
const cropScaleValue = ref(1);
const croppedImage: File | Ref = ref(null);
const isProcessing: boolean | Ref = ref(false);
const emits = defineEmits(["close", "croppedImg"]);
const zoomInBtnColor: Ref = ref("#B7B7B7");
const zoomOutBtnColor: Ref = ref("#B7B7B7");
const dragBtnColor: Ref = ref("#B7B7B7");
const isMacOS = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

watch(cropScaleValue, () => {
  isProcessing.value = true;
});

watch(props, () => {
  if (!props.visible) {
    cropScaleValue.value = 1;
  }
});

const setCroppedImg = async () => {
  const cropperInstance = cropper.value;
  const { canvas } = cropperInstance.getResult();
  if (props.srcType === "image/jpeg") {
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  croppedImage.value = canvas;
  isProcessing.value = false;
  emits("croppedImg", croppedImage.value);
};

const zoomIn = () => {
  const cropperInstance = cropper.value;
  cropperInstance.zoom(1.2);
};

const zoomOut = () => {
  const cropperInstance = cropper.value;
  cropperInstance.zoom(1 / 1.2);
};
</script>

<style lang="scss">
.overlay {
  background: #231f20;
  opacity: 0.5;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
}

.cropper {
  width: 600px;
  height: 600px;

  &__background {
    background: repeating-conic-gradient(#eeeeee 0% 25%, transparent 0% 50%) 50% /
      20px 20px !important;
  }

  &__foreground {
    opacity: 0.1 !important;
  }
}

.overlay-rectangle {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.5) calc(50% - 120px),
    transparent calc(50% - 120px),
    transparent calc(50% + 120px),
    rgba(0, 0, 0, 0.5) calc(50% + 120px),
    rgba(0, 0, 0, 0.5) 100%
  );
  pointer-events: none;
}

.overlay-circle {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle,
    transparent 300px,
    /* Radius of the transparent circle */ rgba(0, 0, 0, 0.5) 300px
  );
  pointer-events: none;
}

.custom-cropper-popover {
  top: 40px !important;
  font-family: Poppins !important;

  .popover-arrow {
    transform: rotate(45deg) !important;
    width: 7px !important;
    height: 7px !important;
    background: white !important;
    top: -4px !important;
    left: 12px !important;
  }
}

@media only screen and (max-width: 768px) {
  .cropper-container {
    align-items: center;
  }

  .cropper {
    width: 300px;
    height: 300px;
  }

  .overlay-rectangle {
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.5) calc(50% - 60px),
      transparent calc(50% - 60px),
      transparent calc(50% + 60px),
      rgba(0, 0, 0, 0.5) calc(50% + 60px),
      rgba(0, 0, 0, 0.5) 100%
    );
  }

  .overlay-circle {
    background: radial-gradient(
      circle,
      transparent 150px,
      /* Radius of the transparent circle */ rgba(0, 0, 0, 0.5) 150px
    );
  }
}
</style>
